import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ApiService from "../services/ApiService";

const schema = Yup.object({
  username: Yup.string().trim().required("Email or mobile is required"),
  password: Yup.string().required("Password is required"),
});

const STATS = [
  { value: "3.2M+", label: "Events tracked daily" },
  { value: "98%", label: "Data accuracy" },
  { value: "12+", label: "Integrations" },
];

export function SignInPage() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Left dark panel */}
      <Box sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        width: 460, flexShrink: 0,
        bgcolor: "#0F172A", p: 5,
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", top: -100, right: -100, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,91,214,0.3) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(14,165,233,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />

        {/* Brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, position: "relative" }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(91,91,214,0.5)" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#F1F5F9", lineHeight: 1.2 }}>OrkaAnalytics</Typography>
            <Typography sx={{ fontSize: "0.65rem", color: "#475569", letterSpacing: "0.04em" }}>Marketing platform</Typography>
          </Box>
        </Box>

        {/* Headline */}
        <Box sx={{ position: "relative" }}>
          <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#F1F5F9", lineHeight: 1.2, letterSpacing: "-0.03em", mb: 2 }}>
            All your marketing data,{" "}
            <Box component="span" sx={{ background: "linear-gradient(90deg, #818CF8 0%, #38BDF8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              in one place.
            </Box>
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#64748B", lineHeight: 1.75 }}>
            Connect Facebook, Instagram, Google Analytics and more. Unified insights without switching tabs.
          </Typography>
          <Box sx={{ display: "flex", gap: 4, mt: 4 }}>
            {STATS.map((s) => (
              <Box key={s.label}>
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#F1F5F9", lineHeight: 1 }}>{s.value}</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#475569", mt: 0.5 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Testimonial */}
        <Box sx={{ bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, p: 3 }}>
          <Typography sx={{ fontSize: "0.875rem", color: "#94A3B8", lineHeight: 1.75, fontStyle: "italic" }}>
            "OrkaAnalytics cut our reporting time in half. We finally have a single source of truth across all ad platforms."
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2.5 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#fff" }}>SR</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#E2E8F0" }}>Sarah R.</Typography>
              <Typography sx={{ fontSize: "0.7rem", color: "#475569" }}>Head of Growth, Lumio</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right form panel */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#F0F2F7", p: { xs: 3, sm: 5 } }}>
        {/* Mobile brand */}
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A" }}>OrkaAnalytics</Typography>
        </Box>

        <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E8EDF5", p: { xs: 3, sm: 4.5 }, width: "100%", maxWidth: 420, boxShadow: "0px 4px 24px rgba(15,23,42,0.07)" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#0F172A", mb: 0.75 }}>Welcome back</Typography>
          <Typography variant="body2" sx={{ color: "#64748B", mb: 3 }}>Sign in to your OrkaAnalytics account</Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>{error}</Alert>}

          <Formik
            initialValues={{ username: "", password: "" }}
            validationSchema={schema}
            onSubmit={async (values, { setSubmitting }) => {
              setError(null);
              try {
                await ApiService.signin(values.username.trim(), values.password);
                navigate("/", { replace: true });
              } catch (e) {
                setError(e?.response?.data?.message || e?.message || "Sign in failed. Try again.");
              } finally { setSubmitting(false); }
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form noValidate>
                <TextField fullWidth name="username" label="Email or mobile" margin="normal"
                  value={values.username} onChange={handleChange} onBlur={handleBlur}
                  error={touched.username && Boolean(errors.username)} helperText={touched.username && errors.username}
                  autoComplete="username" autoFocus />
                <TextField fullWidth name="password" label="Password" type={showPassword ? "text" : "password"} margin="normal"
                  value={values.password} onChange={handleChange} onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)} helperText={touched.password && errors.password}
                  autoComplete="current-password"
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end" tabIndex={-1}>
                        {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )}} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75, mb: 3 }}>
                  <Link component={RouterLink} to="/forgot-password" sx={{ fontSize: "0.8125rem", color: "#5B5BD6", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                    Forgot password?
                  </Link>
                </Box>
                <Button fullWidth type="submit" variant="contained" size="large" disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ py: 1.25, fontSize: "0.9375rem" }}>
                  {isSubmitting ? "Signing in…" : "Sign in"}
                </Button>
              </Form>
            )}
          </Formik>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" sx={{ color: "#94A3B8", px: 1 }}>New to OrkaAnalytics?</Typography>
          </Divider>
          <Button fullWidth variant="outlined" component={RouterLink} to="/signup" sx={{ py: 1.125 }}>
            Create a free account
          </Button>
        </Box>

        <Typography variant="caption" sx={{ color: "#94A3B8", mt: 4 }}>
          © {new Date().getFullYear()} OrkaAnalytics · All rights reserved
        </Typography>
      </Box>
    </Box>
  );
}
